import { useCurrentLocation } from 'app/main/settings/locations/useCurrentLocation';
import { useNonDeletedLocationSummaries } from 'app/main/settings/locations/useNonDeletedLocationSummaries';
import { FormikContextType } from 'formik';
import { setFormValue } from 'framework/forms/utils/setFormValue';
import { useApiCall } from 'framework/hooks/useApiCall';
import { ILocationSummary, IPatient, IRequestForInsuranceAllowance, patientsQuery_single } from 'gen/ApiClient';
import { useEffect, useMemo, useState } from 'react';

export interface IFromLocationRequest {
	fromLocationId: string;
}

export type UseFromLocationInterceptorReturn = {
	locations: ILocationSummary[];
	defaultedLocation: ILocationSummary;
};

export const useFromLocationInterceptor = <TRequest extends IFromLocationRequest>(
	props: FormikContextType<TRequest>,
	patientId: string | undefined,
	rfia?: IRequestForInsuranceAllowance
): UseFromLocationInterceptorReturn => {
	const loadPatient = useApiCall(patientsQuery_single);
	const [locations] = useNonDeletedLocationSummaries();
	const currentLocation = useCurrentLocation();
	const [patient, setPatient] = useState<IPatient>();
	const fromLocationId = useMemo(() => props.values.fromLocationId, [props.values.fromLocationId]);
	const fromLocation = useMemo(() => locations.find(t => t.id === fromLocationId), [fromLocationId, locations]);

	useEffect(() => {
		const loadAndSetPatient = async (patientId: string) => {
			const r = await loadPatient(patientId);
			setPatient(r.result);
		};

		if (patientId) {
			loadAndSetPatient(patientId);
		}
		// eslint-disable-next-line
	}, [patientId]);

	useEffect(() => {
		if (locations && rfia && rfia.locationId) {
			setFormValue<TRequest>('fromLocationId', rfia.locationId, props);
		} else if (locations && patient && patient.locationId) {
			setFormValue<TRequest>('fromLocationId', patient.locationId!, props);
		} else if (locations && currentLocation) {
			setFormValue<TRequest>('fromLocationId', currentLocation.id, props);
		}
		// eslint-disable-next-line
	}, [locations, currentLocation, patient, rfia]);

	return { locations: locations, defaultedLocation: fromLocation ?? currentLocation! };
};
